import { useEffect } from 'react'

import { useHistory } from 'react-router-dom'

type History = ReturnType<typeof useHistory>

/**
 * The keys of the entries this app pushed.
 *
 * A location's `key` says an entry was written, not who wrote it: a
 * `<Redirect>` stamps one during bootstrap, and a browser Back onto a pushed
 * entry still carries the key it was given. Neither `key` nor `action` can
 * answer "is there an in-app page behind this one", so the pushes are watched
 * instead.
 *
 * Module-level and never torn down, because the push being recorded usually
 * happens on a page that has no Back button of its own.
 */
const pushedKeys = new Set<string>()

let watched: History | undefined

const watchPushes = (history: History) => {
  if (watched === history) return
  watched = history

  history.listen((location, action) => {
    if (action === 'PUSH' && location.key !== undefined) {
      pushedKeys.add(location.key)
    }
  })
}

/**
 * The single navigation touchpoint for engine pages (routing discipline from
 * sc-76790): the v5→v7 upgrade swaps useHistory → useNavigate in this one
 * module.
 */
const useResourceNavigate = (): ((path: string) => void) => {
  const history = useHistory()

  useEffect(() => watchPushes(history), [history])

  return (path) => history.push(path)
}

/**
 * Steps back out of a detail or create page, landing on `fallbackPath` when
 * this app did not put the user here.
 *
 * A deep link, a pasted URL, a cold load and a redirect all leave nothing of
 * ours to step back to, so those go to the fallback rather than out of the
 * app entirely.
 */
export const useResourceBack = (): ((fallbackPath: string) => void) => {
  const history = useHistory()

  useEffect(() => watchPushes(history), [history])

  return (fallbackPath) => {
    const { key } = history.location

    if (key !== undefined && pushedKeys.has(key)) {
      history.goBack()
      return
    }

    history.push(fallbackPath)
  }
}

export default useResourceNavigate
